import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Spinner } from "../../shared/ui";
import { pdfjs } from "react-pdf";

// Load the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const Footer = ({ pageNumber, numPages }) => {
  return (
    <div className="flex justify-end w-full max-w-4xl" key={`page_${pageNumber}`}>
      <span className="text-xs mb-6 mt-0 text-muted">
        Page {pageNumber} of {numPages}
      </span>
    </div>
  );
};

const PdfViewer = ({ fileUrl, initialPage }) => {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);

  const pageRefs = useRef(new Map());
  
  const scrollToPage = () => {
    const pageNode = pageRefs.current.get(initialPage);
    if (pageNode) {
      pageNode.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  useEffect(() => {
    setLoading(true);
    const timeoutId = setTimeout(scrollToPage, 1000);
    setLoading(false);
    return () => clearTimeout(timeoutId);
  }, [initialPage, fileUrl, numPages]);

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    pageRefs.current.clear();
  };

  const setPageRef = (pageNumber, node) => {
    if (node) {
      pageRefs.current.set(pageNumber, node);
    } else {
      pageRefs.current.delete(pageNumber);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full w-full py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center bg-gray-100 h-[70vh] overflow-y-auto py-6 border border-line rounded-lg">
      <Document
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={
          <div className="flex justify-center items-center py-10">
            <Spinner />
          </div>
        }
        error={<div className="text-status-critical font-semibold p-4">Failed to load PDF file.</div>}
        className="flex flex-col items-center w-full"
      >
        {numPages &&
          Array.from(new Array(numPages), (el, index) => {
            const pageNumber = index + 1;
            return (
              <div key={`page_wrapper_${pageNumber}`} className="flex flex-col items-center w-full">
                <div
                  ref={(node) => setPageRef(pageNumber, node)}
                  className="mb-2 shadow-lg bg-white"
                >
                  <Page
                    pageNumber={pageNumber}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    scale={1.3}
                  />
                </div>
                <Footer pageNumber={pageNumber} numPages={numPages} />
              </div>
            );
          })}
      </Document>
    </div>
  );
};

PdfViewer.defaultProps = {
  initialPage: 1,
};

PdfViewer.propTypes = {
  fileUrl: PropTypes.string,
  initialPage: PropTypes.number,
};

Footer.propTypes = {
  pageNumber: PropTypes.number,
  numPages: PropTypes.number,
};

export default PdfViewer;
